import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store/configureStore';
import { notification, theme } from 'antd';
import i18nIsoCountries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import { tppOp } from 'store/ducks/DataEntry/ThirdPartyProviders';
import useFilters from 'utils/hooks/useFilters';
import CustomTable from 'components/CustomTable/CustomTable';
import {
  setSelectedTPP,
  set_currencyAndCosts,
  set_isCurrencyAndCostsAdded,
} from 'store/ducks/DataEntry/ThirdPartyProviders/tppSlice';
import { ICurrencyAndCosts } from 'store/ducks/DataEntry/ThirdPartyProviders/types';
import useTppStatus from 'utils/hooks/useTppStatus';
import { entityOp } from 'store/ducks/DataEntry/Entities';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import { getCurrencyAndCostsColumns } from 'components/Columns/CurrencyAndCostsTableColumns';
import { requiredFieldsForCurrencyAndCosts } from 'constants/requiredFields';
import { useTranslation } from 'react-i18next';
import SaveButton from 'components/SaveButton/SaveButton';
import * as Styled from '../../DataEntry.styled';

const CurrencyAndCosts: React.FC = () => {
  useTppStatus();
  const dispatch = useDispatch();
  i18nIsoCountries.registerLocale(enLocale);
  const { query } = useFilters();
  const { useToken } = theme;
  const { token } = useToken();
  const { t } = useTranslation();

  const [dataSource, setDataSource] = useState<ICurrencyAndCosts[]>([]);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [count, setCount] = useState(1);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState(10);
  const [currencies, setCurrencies] = useState<
    { label: string; value: string }[]
  >([]);
  const [costs, setCosts] = useState<{ label: string; value: string }[]>([]);
  const [arrangements, setArrangements] = useState<
    { label: string; value: string }[]
  >([]);
  const [tppNames, setTppNames] = useState<{ label: string; value: string }[]>(
    [],
  );
  const {
    selectedTPP,
    tpps,
    currencyAndCosts,
    addRowCountTpp,
    tppId,
    nameOfTpp,
  } = useSelector((state: RootState) => state.tpp);

  const [selectedTPPID, setSelectedTPPID] = useState<string>('');
  const [combinedData, setCombinedData] = useState<ICurrencyAndCosts[]>([]);
  const [initialData, setInitialData] = useState<ICurrencyAndCosts[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [tppName, setTppName] = useState<string>('');
  const isLoggedIn = localStorage.getItem('authToken');

  const handlePageChange = (page: number, pageSize: number) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  useEffect(() => {
    setCombinedData([...dataSource, ...currencyAndCosts]);
  }, [dataSource, currencyAndCosts]);

  const getCurrencyAndCosts = async () => {
    try {
      const response = await tppOp.setCurrencyAndCosts(tppName, selectedTPPID);
      const mappedData = response.costs.map(
        (
          cost: {
            end_date: string;
            start_date: string;
            currency: { id: any; name: any; description: any };
            id: any;
            currency_amount_reported: any;
            total_annual_expense: any;
            description: any;
            third_party_service_provider_id: any;
          },
          index: any,
        ) => ({
          key: index,
          currency_id: cost.currency?.id ?? '',
          currency_name: cost.currency?.name ?? '',
          currency_description: cost.currency?.description ?? '',
          id: cost?.id ?? '',
          currency_amount_reported: cost?.currency_amount_reported ?? '',
          total_annual_expense: cost?.total_annual_expense ?? '',
          cost_description: cost?.description ?? '',
          third_party_service_provider_id:
            cost?.third_party_service_provider_id ?? '',
          start_date: cost?.start_date ?? '',
          end_date: cost?.end_date ?? '9999-01-01',
          isNew: false,
          isEdited: false,
          tpp_name: response?.tpp_name ?? '',
        }),
      );

      setDataSource(mappedData);
      setCount(mappedData.length);
      setInitialData(mappedData);
    } catch (error) {
      const newRow = {
        key: generateUniqueId(),
        id: generateUniqueId(),
        currency_id: '',
        currency_name: '',
        currency_description: '',
        // id: '',
        currency_amount_reported: null,
        total_annual_expense: null,
        cost_description: '',
        third_party_service_provider_id: selectedTPPID || '',
        start_date: '',
        end_date: '9999-01-01',
        tpp_name: tppName,
        active: true,
        isNew: true,
      };

      setDataSource((prevData) => [...prevData, newRow]);
      setCount((prevCount) => prevCount + 1);
      setInitialData((prevData) => [...prevData, newRow]);
    }
  };

  const fetchTPPID = async () => {
    try {
      const response = await tppOp.fetchTPPbyEntityName({
        entity_id: query.entityId,
        entity_name: query.entity,
      });
      const data = response?.data.third_party_service_providers;
      const dataTPP = data.map((item: any, index: number) => ({
        third_party_service_provider_name:
          item.third_party_service_provider_name,
        id: item.id,
        active: item.active,
      }));

      const dataForTable = data.map((item: any) => ({
        label: item?.third_party_service_provider_name || 'Unknown',
        value: item?.id?.toString() || '',
      }));

      setTppNames(dataForTable);

      const matchedTPP = dataTPP.find(
        (item: ICurrencyAndCosts) =>
          item.id.toString() === selectedTPP.toString(),
      );

      if (matchedTPP) {
        setSelectedTPPID(matchedTPP.id);
        setTppName(matchedTPP.third_party_service_provider_name);
        setIsActive(matchedTPP.active);
      }
    } catch (error) {
      console.log(error, 'error');
    }
  };

  const fetchCurrencies = async () => {
    try {
      const response = await entityOp.fetchCurrencies();
      if (response?.data.currencies) {
        const formattedOptions = response.data.currencies.map(
          (reasons: { currency_name: any; id: any }) => ({
            label: reasons.currency_name,
            value: reasons.id,
          }),
        );
        setCurrencies(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchCosts = async () => {
    try {
      const response = await tppOp.fetchCosts();
      if (response?.data.costs) {
        const formattedOptions = response.data.costs.map(
          (reasons: { description: any; id: any }) => ({
            label: reasons.description,
            value: reasons.id,
          }),
        );
        setCosts(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchArrangements = async () => {
    try {
      const response = await tppOp.fetchContractualArrangements();
      if (response?.data.contractual_arrangements) {
        const formattedOptions = response.data.contractual_arrangements.map(
          (reasons: { arrangement_reference_number: any; id: any }) => ({
            label: reasons.arrangement_reference_number,
            value: reasons.id,
          }),
        );
        setArrangements(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (query.entity) {
      if (selectedTPP === '') {
        fetchTPPID();
      } else if (selectedTPP) {
        dispatch(setSelectedTPP(selectedTPP));
      } else if (!selectedTPP) {
        dispatch(setSelectedTPP(''));
        setDataSource([]);
      }
    }
  }, [query.entity]);

  useEffect(() => {
    if (selectedTPP === '' && dataSource.length > 0 && query.entity) {
      setDataSource([]);
      setSelectedTPPID('');
      setHasChanges(false);
    }
  }, [selectedTPP, dataSource, query.entity]);

  useEffect(() => {
    if (selectedTPP && query.entity) {
      fetchTPPID();
      fetchCurrencies();
      fetchCosts();
      fetchArrangements();
    }
  }, [selectedTPP, query.entity]);

  useEffect(() => {
    if (selectedTPPID && query.entity) {
      getCurrencyAndCosts();
    }
  }, [selectedTPPID, query.entity]);

  useEffect(() => {
    if (addRowCountTpp === 0) {
      dispatch(set_currencyAndCosts([]));
    }
  }, [addRowCountTpp, currencyAndCosts.length, dispatch]);

  const handleSave = (row: ICurrencyAndCosts) => {
    setHasChanges(true);
    const isRowInDataSource = dataSource.some((item) => item.key === row.key);

    if (isRowInDataSource) {
      const updatedDataSource = dataSource.map((item) =>
        item.key === row.key ? { ...item, ...row } : item,
      );

      setDataSource(updatedDataSource);
      setCombinedData([...updatedDataSource, ...currencyAndCosts]);
    } else {
      const updatedEntityType = currencyAndCosts.map((item) =>
        item.key === row.key ? { ...item, ...row } : item,
      );
      setCombinedData([...dataSource, ...updatedEntityType]);
      dispatch(set_currencyAndCosts(updatedEntityType));
    }
  };

  const areAllFieldsFilled = (currencyAndCosts: ICurrencyAndCosts[]) => {
    return currencyAndCosts.every((item) => {
      const invalidKeys = Object.entries(item).filter(([key, value]) => {
        if (key === 'description') return false;
        return value === '' || value === null || value === undefined;
      });
      return invalidKeys.length === 0;
    });
  };

  useEffect(() => {
    if (addRowCountTpp === 1) {
      const allFieldsFilled = areAllFieldsFilled(currencyAndCosts);
      if (allFieldsFilled) {
        dispatch(set_isCurrencyAndCostsAdded(true));
      } else {
        dispatch(set_isCurrencyAndCostsAdded(false));
      }
    }
  }, [currencyAndCosts, addRowCountTpp]);

  const handleSubmit = async () => {
    if (combinedData.length === 0) {
      notification.info({
        message: '',
        description: 'No new data has been changed',
        placement: 'topRight',
        duration: 20,
      });
      return;
    }

    const costs_list = combinedData.map((item) => ({
      id: item.id,
      currency_amount_reported: item.currency_amount_reported,
      total_annual_expense: item.total_annual_expense,
      description: item.cost_description,
      currency_id: item.currency_id,
      third_party_service_provider_id: item.third_party_service_provider_id,
      start_date: item.start_date,
      end_date: item.end_date,
    }));

    const validateRows = (rows: any[]) => {
      return rows.some((item) =>
        requiredFieldsForCurrencyAndCosts.some(
          (field) => item[field] === '' || item[field] === null,
        ),
      );
    };

    const newRows = combinedData.filter((row) => row.isNew);
    const updatedRows = combinedData.filter((row) => !row.isNew);

    if (validateRows(newRows) || validateRows(updatedRows)) {
      notification.error({
        message: '',
        description:
          'Please complete all the fields highlighted in yellow and make sure to enter a start date before submitting.',
        placement: 'topRight',
        duration: 20,
      });
      return;
    }

    try {
      const newRows = combinedData.filter((row) => row.isNew);
      const updatedRows = combinedData.filter((row) => !row.isNew);

      for (const newRow of newRows) {
        await tppOp.insertCurrencyAndCosts({
          tpp_name: newRow.tpp_name,
          entity_name: query.entity,
          costs_list,
          token: isLoggedIn,
        });
      }

      if (updatedRows.length > 0) {
        await tppOp.insertCurrencyAndCosts({
          tpp_name: tppName,
          entity_name: query.entity,
          costs_list,
          token: isLoggedIn,
        });
      }

      notification.success({
        message: 'Info',
        description: 'The data has been submitted successfully.',
        placement: 'topRight',
        duration: 20,
      });
      setDataSource((prev) =>
        prev.map((item) => ({ ...item, isNew: false, isEdited: false })),
      );
      setHasChanges(false);
    } catch (error) {
      notification.error({
        message: 'Info',
        description: 'There was an error sending the data. Please try again.',
        placement: 'topRight',
        duration: 20,
      });
    }
  };

  const columns = getCurrencyAndCostsColumns(
    handleSave,
    arrangements,
    currencies,
    t,
  );
  return (
    dataSource && (
      <Styled.StyledWrapper hasEntity={!!query.entity}>
        <Styled.HighlightedTable>
          <CustomTable
            columns={columns}
            dataSource={combinedData}
            handleSave={handleSave}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              onChange: handlePageChange,
              showSizeChanger: true,
            }}
          />
        </Styled.HighlightedTable>

        {query.entity && (
          <SaveButton
            token={token}
            hasChanges={hasChanges}
            onClick={handleSubmit}
          />
        )}
      </Styled.StyledWrapper>
    )
  );
};

export default CurrencyAndCosts;
